import { useEffect, useState } from 'react';
import { injectTokenVariables } from './design/tokens.js';
import { SessionProvider, useSession } from './lib/auth/session.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { LibraryScreen } from './screens/LibraryScreen.js';
import { OnboardingScreen } from './screens/OnboardingScreen.js';
import { authApi } from './lib/api/auth.js';
import { isOnboardingDone, markOnboardingDone } from './lib/onboarding.js';

injectTokenVariables();

/**
 * Routes between the first-run wizard, the login screen, and the library.
 *
 * A fresh server (`firstRun` — no password set yet, see POST /auth/setup)
 * shows the onboarding wizard INSTEAD of the login screen, so a brand-new
 * user never meets a login wall. The wizard sets the password (which issues
 * the session), configures optional sync, then lands in the library.
 * Everything after that is the normal login flow.
 */
function Router() {
  const { state, refresh } = useSession();
  /** Server-side first-run flag; null while unknown. */
  const [firstRun, setFirstRun] = useState<boolean | null>(null);

  useEffect(() => {
    document.title = 'Panda Note';
  }, []);

  useEffect(() => {
    if (state !== 'signedOut') return;
    let cancelled = false;
    void authApi
      .health()
      .then((health) => {
        if (!cancelled) setFirstRun(health.firstRun);
      })
      .catch(() => {
        // Server unreachable: fall back to the login screen, which has its
        // own error handling for a dead server.
        if (!cancelled) setFirstRun(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === 'checking' || (state === 'signedOut' && firstRun === null)) {
    return <div className="app-loading">Loading…</div>;
  }

  if (state === 'signedIn') {
    return <LibraryScreen />;
  }

  if (firstRun && !isOnboardingDone()) {
    return (
      <OnboardingScreen
        onFinished={() => {
          markOnboardingDone();
          // Setup issued a session, so this lands in the library; if the
          // session is somehow gone the router falls back to the login screen.
          void refresh();
        }}
      />
    );
  }

  return <LoginScreen />;
}

export function App() {
  return (
    <SessionProvider>
      <Router />
    </SessionProvider>
  );
}
